import type {
  AnswersByKey,
  EmailStep,
  FlowVariant,
  FunnelOption,
  FunnelOptionFeedback,
  FunnelStep,
  LoadingStep,
  PaymentPlanOption,
  PaymentStep,
  QuestionStep,
  ResultStep,
  WelcomeStep,
} from "./types";
import { config } from "../config";

export const COOKY_SOCIAL_PROOF = {
  rating: "4.7",
  reviewCount: "540+",
} as const;

export const COOKY_SOCIAL_PROOF_INLINE = `${COOKY_SOCIAL_PROOF.rating} ★ · ${COOKY_SOCIAL_PROOF.reviewCount} reviews`;

export const COOKY_RESULT_PROFILE_ITEMS = [
  "18+ pantry-first recipes under 20 minutes",
  "A 7-day meal plan matched to your household",
  "A shopping list built around your biggest blocker",
] as const;

export const COOKY_EMAIL_VALUE_STACK = [
  "18+ pantry-first recipes matched to you",
  "7-day meal plan + automatic shopping list",
  "Dinner decided in under 60 seconds",
] as const;

/**
 * Single source of truth for plan pricing.
 * Update prices here only. Original price is derived as totalPrice / (1 - savePercent/100).
 * Per-week is derived as totalPrice / weeksInPeriod.
 */
export const COOKY_PLANS: PaymentPlanOption[] = [
  {
    id: "monthly",
    label: "1 month",
    savePercent: 40,
    weeksInPeriod: 4.33,
    totalPrice: "$9.99",
  },
  {
    id: "quarterly",
    label: "3 months",
    savePercent: 50,
    weeksInPeriod: 13,
    totalPrice: "$24.99",
    isMostPopular: true,
  },
  {
    id: "annual",
    label: "1 year",
    savePercent: 60,
    weeksInPeriod: 52,
    totalPrice: "$47.00",
  },
];

export const COOKY_TRUST_BADGE_AVATARS = [
  { src: "/cooky/testimonials/girl-1.webp", alt: "Maya" },
  { src: "/cooky/testimonials/man-1.webp", alt: "Chris" },
  { src: "/cooky/testimonials/girl-2.webp", alt: "Priya" },
  { src: "/profile_tiny_jess.webp", alt: "Jess" },
] as const;

export const COOKY_WELCOME_RATING_TEXT = `<b>${COOKY_SOCIAL_PROOF.rating}</b> <span class="welcome-trust-star">★</span> rated · <b>${COOKY_SOCIAL_PROOF.reviewCount}</b> reviews`;
export const COOKY_WELCOME_TRUST_BADGE_CAPTION =
  "Trusted by home cooks like Maya, Chris, Priya...";
export const COOKY_FEATURED_IN_PUBLICATIONS = [
  "Men's Health",
  "POPSUGAR",
  "The Kitchn",
] as const;

type FlowValue<T> = T | { a: T; b: T };

const split = <T>(a: T, b: T): FlowValue<T> => ({ a, b });

const isSplitValue = <T>(value: FlowValue<T>): value is { a: T; b: T } => {
  return (
    typeof value === "object" && value !== null && "a" in value && "b" in value
  );
};

const resolveByFlow = <T>(value: FlowValue<T>, flow: FlowVariant): T => {
  return isSplitValue(value) ? value[flow] : value;
};

type RawWelcomeStep = Omit<
  WelcomeStep,
  | "title"
  | "description"
  | "ratingText"
  | "trustBadgeCaption"
  | "trustBadgeAvatars"
  | "featuredIn"
  | "embeddedQuestion"
  | "heroImage"
  | "confirmLabel"
  | "heroSlides"
  | "featurePills"
  | "finePrint"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  ratingText: FlowValue<string>;
  trustBadgeCaption?: FlowValue<string>;
  trustBadgeAvatars?: FlowValue<WelcomeStep["trustBadgeAvatars"]>;
  featuredIn?: FlowValue<string[]>;
  embeddedQuestion?: FlowValue<WelcomeStep["embeddedQuestion"]>;
  heroImage?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  heroSlides?: FlowValue<WelcomeStep["heroSlides"]>;
  featurePills?: FlowValue<WelcomeStep["featurePills"]>;
  finePrint?: FlowValue<string>;
};

type RawQuestionStep = Omit<
  QuestionStep,
  "title" | "subtitle" | "helperText" | "options" | "mediaLabel" | "mediaImage"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  helperText?: FlowValue<string>;
  options: FlowValue<FunnelOption[]>;
  mediaLabel?: FlowValue<string>;
  mediaImage?: FlowValue<string>;
  optionFeedback?: Record<string, FunnelOptionFeedback>;
};

type RawAffirmationStep = Omit<
  Extract<FunnelStep, { kind: "affirmation" }>,
  "title" | "description" | "confirmLabel" | "mediaLabel" | "heroImage"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  heroImage?: FlowValue<string>;
  mediaLabel?: FlowValue<string>;
};

type RawLoadingStep = Omit<
  LoadingStep,
  "title" | "subtitle" | "confirmLabel" | "loadingFact"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  loadingFact: FlowValue<string>;
  heroImage?: FlowValue<string>;
};

type RawResultStep = Omit<
  ResultStep,
  | "title"
  | "subtitle"
  | "personaName"
  | "description"
  | "questMap"
  | "confirmLabel"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  personaName: FlowValue<string>;
  description: FlowValue<string[]>;
  questMap: FlowValue<ResultStep["questMap"]>;
  confirmLabel: FlowValue<string>;
};

type RawEmailStep = Omit<
  EmailStep,
  "title" | "subtitle" | "description" | "confirmLabel" | "secondaryCta"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  secondaryCta?: FlowValue<string>;
};

type RawPaymentStep = Omit<
  PaymentStep,
  | "title"
  | "subtitle"
  | "description"
  | "benefits"
  | "plans"
  | "proofBullets"
  | "testimonials"
  | "confirmLabel"
  | "whyCardCopy"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  benefits: FlowValue<string[]>;
  plans: FlowValue<PaymentStep["plans"]>;
  proofBullets: FlowValue<string[]>;
  testimonials: FlowValue<PaymentStep["testimonials"]>;
  confirmLabel: FlowValue<string>;
  whyCardCopy: FlowValue<string>;
};

type RawStep =
  | RawWelcomeStep
  | RawQuestionStep
  | RawAffirmationStep
  | RawLoadingStep
  | RawResultStep
  | RawEmailStep
  | RawPaymentStep;

const yesNoOptions: FunnelOption[] = [
  { id: "yes", label: "Yes", emoji: "✅" },
  { id: "no", label: "No", emoji: "❌" },
];

const COOKING_FOR_OPTIONS: FunnelOption[] = [
  {
    id: "just_me",
    label: "Just me",
    image: "/cooky/mascot/mascot-quick-meals.webp",
  },
  {
    id: "partner",
    label: "Me and my partner",
    image: "/cooky/mascot/mascot-family-meals.webp",
  },
  {
    id: "family",
    label: "Family",
    image: "/cooky/mascot/mascot-family-meals.webp",
  },
  {
    id: "friends_entertaining",
    label: "Friends & entertaining",
    image: "/cooky/mascot/mascot-photo-of-ingredients.webp",
  },
];

/** Flow B welcome — pain point for saved recipes (embedded before quiz steps). */
const SAVED_RECIPES_EMBED_OPTIONS: FunnelOption[] = [
  {
    id: "forget_about_them",
    label: "I forget about them",
    description: "Out of sight, out of mind.",
    image: "/cooky/screen-2/image%2061.png",
  },
  {
    id: "save_too_many",
    label: "I save too many",
    description: "So many ideas, so little time.",
    image: "/cooky/screen-2/image%2062.png",
  },
  {
    id: "wrong_ingredients",
    label: "I never have the right ingredients",
    description: "Missing one thing ruins the plan.",
    image: "/cooky/screen-2/image%2063.png",
  },
];

/** Screen 2 — pulled forward from former mid-funnel blocker (simplified to 3 paths). */
const DINNER_EASIER_OPTIONS: FunnelOption[] = [
  {
    id: "quick_from_have",
    label: "Quick meals from what I already have",
    description: "No extra shopping. No overthinking.",
    image: "/cooky/mascot/mascot-quick-meals.webp",
  },
  {
    id: "use_what_i_save",
    label: "Actually use what I save",
    description: "Turn ideas into real dinners.",
    image: "/cooky/mascot/mascot-import.webp",
  },
  {
    id: "less_thinking_evenings",
    label: "Less thinking after a long day",
    description: "Just tell me what to cook.",
    image: "/cooky/mascot/mascot-family-meals.webp",
  },
];

// ─── Persona system ──────────────────────────────────────────────────────────

type PersonaKey =
  | "pantry_raider"
  | "steady_cook"
  | "explorer"
  | "planner"
  | "natural"
  | "kitchen_devotee";

type PersonaData = {
  heroImage: string;
  personaName: string;
  description: string[];
  questMap: ResultStep["questMap"];
};

type PersonaCopy = Omit<PersonaData, "questMap">;

const PERSONAS: Record<PersonaKey, PersonaCopy> = {
  pantry_raider: {
    heroImage: "/cooky/mascot/mascot-quick-meals.webp",
    personaName: "The Pantry Raider",
    description: [
      "You open the fridge and see dinner. No script, no stress. Just instinct and whatever's on hand. Cooky helps you turn that spontaneity into meals you actually want to eat.",
    ],
  },
  steady_cook: {
    heroImage: "/cooky/mascot/mascot-empty-search.webp",
    personaName: "The Steady Cook",
    description: [
      "You like recipes you can trust and rhythms you can repeat. Cooky keeps your staples feeling fresh so your go-to meals never get boring.",
    ],
  },
  explorer: {
    heroImage: "/cooky/mascot/mascot-photo-of-ingredients.webp",
    personaName: "The Explorer",
    description: [
      "New cuisines, new techniques, new aisles. You treat the kitchen like a playground. Cooky feeds your curiosity with ideas that match the energy you bring.",
    ],
  },
  planner: {
    heroImage: "/cooky/mascot/mascot-empty-search.webp",
    personaName: "The Planner",
    description: [
      "Lists, prep, and a plan beat chaos every time. Cooky lines up meals and ingredients so your week runs smooth without losing flavor.",
    ],
  },
  natural: {
    heroImage: "/cooky/mascot/mascot-import.webp",
    personaName: "The Natural",
    description: [
      "You taste, tweak, and trust your senses more than a timer. Cooky backs your intuition with just enough structure so every experiment still lands.",
    ],
  },
  kitchen_devotee: {
    heroImage: "/cooky/mascot/mascot-learning-skills.webp",
    personaName: "The Kitchen Devotee",
    description: [
      "You care about the how: the method, the detail, the craft. Cooky goes deep on technique so your effort shows up on the plate.",
    ],
  },
};

export const getPersonaHeroImages = (): string[] => {
  return Object.values(PERSONAS).map((persona) => persona.heroImage);
};

const PERSONA_ALIASES: Record<string, PersonaKey> = {
  pantry_raider: "pantry_raider",
  pantryraider: "pantry_raider",
  steady_cook: "steady_cook",
  steadycook: "steady_cook",
  explorer: "explorer",
  planner: "planner",
  natural: "natural",
  kitchen_devotee: "kitchen_devotee",
  kitchendevotee: "kitchen_devotee",
};

const normalizePersonaAlias = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "_");
};

const resolvePersonaOverride = (value: string | null): PersonaKey | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizePersonaAlias(value);
  return PERSONA_ALIASES[normalized] ?? null;
};

const str = (answers: AnswersByKey, key: string): string =>
  typeof answers[key] === "string" ? answers[key] : "";

const arr = (answers: AnswersByKey, key: string): string[] =>
  Array.isArray(answers[key]) ? (answers[key] as string[]) : [];

const computeTraitMap = (answers: AnswersByKey): ResultStep["questMap"] => {
  const why = arr(answers, "your_why");

  const mealCreativity: Record<string, number> = {
    quick_15: 1,
    one_pot: 1,
    healthy: 2,
    adventurous: 4,
  };
  const cuisine1Know: Record<string, number> = {
    yes_absolutely: 3,
    something_french: 2,
    heard_not_sure: 1,
    no_idea: 0,
  };
  const cuisine2Fridge: Record<string, number> = {
    all_the_time: 3,
    sometimes: 2,
    tried_once: 1,
    never: 0,
  };

  const creativityRaw =
    (mealCreativity[str(answers, "meal_preference")] ?? 2) +
    (why.includes("enjoy_kitchen") ? 1 : 0) +
    (cuisine1Know[str(answers, "cuisine_check_1")] ?? 1) +
    (str(answers, "rapid_fire_1") === "yes" ? 1 : 0) +
    (str(answers, "rapid_fire_2") === "yes" ? 1 : 0) +
    (str(answers, "new_ingredients") === "yes" ? 1 : 0);

  const weeklyScores: Record<string, number> = {
    rarely: 0,
    one_two: 1,
    three_four: 2,
    almost_daily: 3,
  };
  const confidenceScores: Record<string, number> = {
    beginner: 0,
    follow_recipe: 1,
    comfortable: 2,
    masterchef: 3,
  };
  const cookingForScores: Record<string, number> = {
    just_me: 0,
    partner: 1,
    family: 2,
    friends_entertaining: 2,
  };

  const commitmentRaw =
    (weeklyScores[str(answers, "weekly_cooking")] ?? 1) +
    (confidenceScores[str(answers, "cooking_confidence")] ?? 1) +
    (cookingForScores[str(answers, "cooking_for")] ?? 1);

  const knowledgeRaw =
    (confidenceScores[str(answers, "cooking_confidence")] ?? 1) +
    (cuisine1Know[str(answers, "cuisine_check_1")] ?? 1) +
    (cuisine2Fridge[str(answers, "cuisine_check_2")] ?? 1);

  const depthIntent: Record<string, number> = {
    simple: 1,
    full_picture: 2,
    real_depth: 3,
  };

  const intentionalityRaw =
    (depthIntent[str(answers, "cooking_depth")] ?? 2) +
    Math.min(why.length, 5) +
    (why.includes("eat_healthier") ? 1 : 0) +
    (why.includes("family_enjoys") ? 1 : 0) +
    (why.includes("waste_stress") ? 1 : 0) +
    (why.includes("enjoy_kitchen") ? 1 : 0);

  const BASE = 2;
  const creativity = creativityRaw + BASE;
  const commitment = commitmentRaw + BASE;
  const knowledge = knowledgeRaw + BASE;
  const intentionality = intentionalityRaw + BASE;
  const total = creativity + commitment + knowledge + intentionality;

  return [
    {
      id: "creativity",
      label: "Creativity",
      percent: Math.round((creativity / total) * 100),
      icon: "🎨",
    },
    {
      id: "commitment",
      label: "Commitment",
      percent: Math.round((commitment / total) * 100),
      icon: "🔥",
    },
    {
      id: "knowledge",
      label: "Knowledge",
      percent: Math.round((knowledge / total) * 100),
      icon: "📚",
    },
    {
      id: "intentionality",
      label: "Intentionality",
      percent: Math.round((intentionality / total) * 100),
      icon: "🎯",
    },
  ].sort((a, b) => b.percent - a.percent);
};

const resolvePersonaKey = (answers: AnswersByKey): PersonaKey => {
  const depthMap: Record<string, "casual" | "moderate" | "deep"> = {
    simple: "casual",
    full_picture: "moderate",
    real_depth: "deep",
  };
  const depth = depthMap[str(answers, "cooking_depth")] ?? "moderate";

  const styleMap: Record<string, "free" | "structured"> = {
    quick_15: "structured",
    one_pot: "structured",
    healthy: "free",
    adventurous: "free",
  };
  const style = styleMap[str(answers, "meal_preference")] ?? "structured";

  if (depth === "casual" && style === "free") return "pantry_raider";
  if (depth === "casual" && style === "structured") return "steady_cook";
  if (depth === "moderate" && style === "free") return "explorer";
  if (depth === "moderate" && style === "structured") return "planner";
  if (depth === "deep" && style === "free") return "natural";
  return "kitchen_devotee";
};

export const getPersonaData = (answers: AnswersByKey): PersonaData => ({
  ...PERSONAS[resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers),
});

export const resolvePersonaOverrideFromUrl = (
  urlLike: URL | string = window.location.href,
): PersonaKey | null => {
  const url = typeof urlLike === "string" ? new URL(urlLike) : urlLike;
  const override =
    resolvePersonaOverride(url.searchParams.get("persona")) ??
    resolvePersonaOverride(url.searchParams.get("persona_override"));
  return override;
};

export const getPersonaDataWithOverride = (
  answers: AnswersByKey,
  overridePersona: PersonaKey | null,
): PersonaData => ({
  ...PERSONAS[overridePersona ?? resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers),
});

// ─────────────────────────────────────────────────────────────────────────────

const RAW_STEPS: RawStep[] = [
  {
    id: "welcome",
    order: 1,
    kind: "welcome",
    heroImage: split(
      "/cooky/Collateral/hero-ad-set-4.webp",
      "/cooky/Collateral/image-use-what-you-have.avif",
    ),
    title: split(
      'Use what you have <span class="welcome-title-underline-wrap"><span class="welcome-title-underline-inner"><span class="welcome-title-underline-text">in the pantry</span></span></span>',
      'Saved recipes <span class="welcome-title-underline-wrap"><span class="welcome-title-underline-inner welcome-title-underline-inner--word"><span class="welcome-title-underline-text">don&#39;t</span></span></span> make dinner',
    ),
    description: split(
      ["Cooky helps you turn saved recipes and pantry ingredients into meals."],
      [
        "Cooky turns your saved recipes into real dinners using ingredients you already have.",
      ],
    ),
    ratingText: split(COOKY_WELCOME_RATING_TEXT, COOKY_WELCOME_RATING_TEXT),
    trustBadgeCaption: split(
      COOKY_WELCOME_TRUST_BADGE_CAPTION,
      COOKY_WELCOME_TRUST_BADGE_CAPTION,
    ),
    trustBadgeAvatars: split(
      [...COOKY_TRUST_BADGE_AVATARS],
      [...COOKY_TRUST_BADGE_AVATARS],
    ),
    featuredIn: split([...COOKY_FEATURED_IN_PUBLICATIONS], []),
    embeddedQuestion: split(undefined, {
      answerKey: "saved_recipes_friction",
      title: "What usually happens to your saved recipes?",
      options: SAVED_RECIPES_EMBED_OPTIONS,
    }),
    confirmLabel: split("Start the 60-second quiz", "Start the 60-second quiz"),
    finePrint: split(
      "No commitment · 60 seconds · No spam",
      "No commitment · 60 seconds · No spam",
    ),
  },
  {
    id: "saved_recipes_friction",
    order: 2,
    kind: "question",
    selection: "single",
    answerKey: "saved_recipes_friction",
    title: "What usually happens to your saved recipes?",
    options: SAVED_RECIPES_EMBED_OPTIONS,
    confirmLabel: "Continue →",
  },
  {
    id: "dinner_easier",
    order: 3,
    kind: "question",
    selection: "single",
    answerKey: "dinner_easier",
    subtitle:
      "Got it. Let\u2019s fix that.",
    title: "What would make dinner easier for you?",
    options: DINNER_EASIER_OPTIONS,
    confirmLabel: "Continue →",
  },
  {
    id: "cooking_confidence",
    order: 4,
    kind: "question",
    selection: "single",
    answerKey: "cooking_confidence",
    title: "How confident are you in the kitchen?",
    options: [
      { id: "beginner", label: "Beginner", emoji: "🌱" },
      { id: "follow_recipe", label: "I can follow a recipe", emoji: "📖" },
      { id: "comfortable", label: "Pretty comfortable", emoji: "👩‍🍳" },
      { id: "masterchef", label: "I could go on MasterChef", emoji: "🏆" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "cooking_for",
    order: 5,
    kind: "question",
    selection: "single",
    answerKey: "cooking_for",
    subtitle: "This helps us tailor your experience to real life.",
    title: "Who are we cooking for most often?",
    options: COOKING_FOR_OPTIONS,
    confirmLabel: "Continue →",
  },
  {
    id: "affirmation_1",
    order: 6,
    kind: "affirmation",
    progress: 20,
    title:
      "Whether you're just starting or leveling up, Cooky meets you where you are.",
    description: [
      "We'll match recipes, tips, and habits to your real life: busy weeknights, picky eaters, empty fridges, all of it.",
    ],
    mediaLabel: "Graphic placeholder",
    confirmLabel: "Show me more →",
  },
  {
    id: "weekly_cooking",
    order: 8,
    kind: "question",
    selection: "single",
    answerKey: "weekly_cooking",
    title: "How many times a week do you cook at home?",
    options: [
      { id: "rarely", label: "Rarely", emoji: "🍕" },
      { id: "one_two", label: "1–2 times", emoji: "🍳" },
      { id: "three_four", label: "3–4 times", emoji: "🥘" },
      { id: "almost_daily", label: "Almost every day", emoji: "✨" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "cuisine_check_1",
    order: 9,
    kind: "question",
    selection: "single",
    answerKey: "cuisine_check_1",
    mediaLabel: "Kitchen skills",
    mediaImage: "/cooky/mascot/mascot-learning-skills.webp",
    title: "Do you know what mise en place means?",
    options: [
      { id: "yes_absolutely", label: "Yes, absolutely", emoji: "✅" },
      { id: "something_french", label: "Something French?", emoji: "🇫🇷" },
      { id: "heard_not_sure", label: "Heard of it, not sure", emoji: "🤔" },
      { id: "no_idea", label: "No idea", emoji: "❓" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "cuisine_check_2",
    order: 10,
    kind: "question",
    selection: "single",
    answerKey: "cuisine_check_2",
    mediaImage: "/cooky/mascot/mascot-photo-of-ingredients.webp",
    title: "Ever made a meal using only what was already in your fridge?",
    options: [
      { id: "all_the_time", label: "All the time", emoji: "🙌" },
      { id: "sometimes", label: "Sometimes", emoji: "👍" },
      {
        id: "tried_once",
        label: "Tried once… questionable results",
        emoji: "😅",
      },
      { id: "never", label: "Never dared", emoji: "🙈" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "meal_preference",
    order: 11,
    kind: "question",
    selection: "single",
    answerKey: "meal_preference",
    title: "What kind of meals do you gravitate toward?",
    options: [
      { id: "quick_15", label: "Quick 15-minute meals", emoji: "⚡" },
      { id: "one_pot", label: "One-pot comfort food", emoji: "🍲" },
      { id: "healthy", label: "Healthy balanced plates", emoji: "🥗" },
      { id: "adventurous", label: "Adventurous new recipes", emoji: "🌶️" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "affirmation_2",
    order: 12,
    kind: "affirmation",
    icon: "🍳",
    progress: 50,
    title: "Cooky is already building your kitchen profile.",
    heroVideo: "/cooky/animated/Family-meal.mp4",
    heroImage: "/cooky/mascot/mascot-family-meals.webp",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    description: [
      "Based on your answers, we're matching you with meals, tips, and a cooking style that fits your life.",
      "<u>No shame</u>, no pressure. Just better dinners, one step at a time.",
    ],
    confirmLabel: "Keep building →",
  },
  {
    id: "cooking_depth",
    order: 13,
    kind: "question",
    selection: "single",
    answerKey: "cooking_depth",
    title: "How deep do you want to go?",
    options: [
      {
        id: "simple",
        label: "Keep it simple",
        description: "Quick recipes, minimal steps",
        emoji: "🌱",
      },
      {
        id: "full_picture",
        label: "Give me the full picture",
        description: "Techniques + tips along the way",
        emoji: "📖",
        badge: "Most popular",
      },
      {
        id: "real_depth",
        label: "I want real depth",
        description: "Plating, techniques, cuisine deep-dives",
        emoji: "🎓",
      },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "rapid_fire_1",
    order: 14,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_1",
    mediaLabel: "Pantry meals",
    mediaImage: "/cooky/mascot/mascot-quick-meals.webp",
    title: "Would you try a 15-minute meal Cooky picks from your pantry?",
    options: yesNoOptions,
    confirmLabel: "Continue →",
  },
  {
    id: "rapid_fire_2",
    order: 15,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_2",
    mediaLabel: "Hands-free cooking",
    mediaImage: "/cooky/mascot/mascot-scan.webp",
    title: "Would you use hands-free cooking mode while you cook?",
    options: yesNoOptions,
    confirmLabel: "Continue →",
  },
  {
    id: "your_why",
    order: 16,
    kind: "question",
    selection: "multi",
    answerKey: "your_why",
    title: "Why do you want to cook more?",
    helperText: "Pick all that apply",
    minSelections: 1,
    options: [
      { id: "eat_healthier", label: "I want to eat healthier", emoji: "🥗" },
      {
        id: "save_takeout",
        label: "I want to save money on takeout",
        emoji: "💸",
      },
      {
        id: "family_enjoys",
        label: "I want meals my family actually enjoys",
        emoji: "👨‍👩‍👧",
      },
      { id: "waste_stress", label: "I'm tired of wasting food", emoji: "♻️" },
      {
        id: "less_stress",
        label: "I want cooking to feel less stressful",
        emoji: "🧘",
      },
      {
        id: "enjoy_kitchen",
        label: "I want to enjoy being in the kitchen",
        emoji: "✨",
      },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "generation_loading",
    order: 17,
    kind: "loading",
    progress: 85,
    title: "Crafting your kitchen profile…",
    subtitle: "Cooky is lining up meals and habits that match your answers.",
    heroVideo: "/cooky/animated/Cooking.mp4",
    heroImage: "/cooky/brand-moment-background.jpg",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    loadingFact:
      "People who cook at home even a few more nights per week often save money, waste less food, and feel more in control of their week.",
    modalQuestion: {
      answerKey: "new_ingredients",
      title: "Quick question: do you like trying new ingredients?",
      options: [
        { id: "yes", label: "Yes" },
        { id: "not_really", label: "Not really" },
      ],
    },
    confirmLabel: "See my results →",
  },
  {
    id: "persona_result",
    order: 18,
    kind: "result",
    icon: "🎯",
    title: "Your kitchen profile is ready.",
    subtitle: "Here's the cooking style we matched from your answers.",
    heroImageMarginBottom: 10,
    personaName: "The Explorer",
    description: [
      "You're ready to cook in a way that fits your real life, not a fantasy version of it.",
      "Cooky will keep nudging you toward meals that match your goals, your time, and your taste.",
    ],
    questMap: [
      { id: "creativity", label: "Creativity", percent: 30, icon: "🎨" },
      { id: "commitment", label: "Commitment", percent: 28, icon: "🔥" },
      { id: "knowledge", label: "Knowledge", percent: 22, icon: "📚" },
      {
        id: "intentionality",
        label: "Intentionality",
        percent: 20,
        icon: "🎯",
      },
    ],
    confirmLabel: "Save my kitchen profile →",
  },
  {
    id: "email_capture",
    order: 19,
    kind: "email",
    answerKey: "email",
    title: "Your kitchen profile is ready.",
    description: ["Where should we send it?"],
    emailPlaceholder: "your@email.com",
    confirmLabel: "Continue with email →",
    trustNote: "Free · Profile reserved for 48h · No spam",
    quotes: [
      {
        text: "I finally stopped ordering takeout four nights a week. Cooky makes it stupid-easy to use what I already bought.",
        author: "Maya R., 34",
        weeksUsingApp: 6,
        image: "/cooky/testimonials/girl-1.webp",
      },
      {
        text: "The pantry meals are unreal. I'm not meal-prepping like a robot. I'm just eating better with less thinking.",
        author: "Chris L., 41",
        weeksUsingApp: 11,
        image: "/cooky/testimonials/man-1.webp",
      },
      {
        text: "Saved my Sunday. Instead of stressing over what to cook, I just open Cooky and it tells me exactly what to make from what's already in my fridge.",
        author: "Sarah T., 29",
        weeksUsingApp: 8,
        image: "/cooky/testimonials/girl-2.webp",
      },
      {
        text: "My grocery bill dropped by a third. I stopped buying random stuff and actually cook what I plan. Game changer.",
        author: "James K., 37",
        weeksUsingApp: 14,
      },
    ],
  },
  {
    id: "founding_member",
    order: 20,
    kind: "payment",
    icon: "🍳",
    answerKey: "selected_plan",
    title: "Choose your plan",
    subtitle: "Founding member",
    description: [
      "Most people want to cook at home more. They just need a system that fits real life. This is it.",
      "Pick the plan that fits your pace. You'll finish checkout securely on Stripe.",
    ],
    plans: COOKY_PLANS,
    benefits: [
      "Founding pricing locked in",
      "Early access to new Cooky features",
      "Your kitchen profile and meal history saved",
    ],
    proofBullets: [
      "Recipes and ideas matched to your pantry and preferences",
      "Shortcuts for busy nights without giving up flavor",
      "Progress you can see: less waste, more home cooking",
    ],
    testimonials: [
      {
        text: "I finally stopped ordering takeout four nights a week. Cooky makes it stupid-easy to use what I already bought.",
        author: "Maya R., 34",
        weeksUsingApp: 6,
        image: "/cooky/testimonials/girl-1.webp",
      },
      {
        text: "The pantry meals are unreal. I'm not meal-prepping like a robot. I'm just eating better with less thinking.",
        author: "Chris L., 41",
        weeksUsingApp: 11,
        image: "/cooky/testimonials/man-1.webp",
      },
      {
        text: "I used to dread the 'what's for dinner' question. Now I have three fast paths that work for my picky kids.",
        author: "Priya S., 38",
        weeksUsingApp: 18,
        image: "/cooky/testimonials/girl-2.webp",
      },
      {
        text: "I'm not a natural cook, but I'm not embarrassed anymore. The step-by-step mode keeps me calm when things get hectic.",
        author: "Daniel K., 45",
        weeksUsingApp: 35,
        image: "/cooky/testimonials/man-1.webp",
      },
      {
        text: "We cut our grocery waste in half because I'm actually using what's in the fridge. That alone paid for it.",
        author: "Elena M., 29",
        weeksUsingApp: 31,
        image: "/cooky/testimonials/girl-1.webp",
      },
    ],
    noChargeNote: "30-day money-back guarantee",
    securityNotes: ["Secure checkout via Stripe", "Cancel anytime"],
    appScreenshots: [
      "/cooky/app-showcase/screenshot-1-600.webp",
      "/cooky/app-showcase/screenshot-2-600.webp",
      "/cooky/app-showcase/screenshot-3-600.webp",
      "/cooky/app-showcase/screenshot-5-600.webp",
      "/cooky/app-showcase/screenshot-6-600.webp",
      "/cooky/app-showcase/screenshot-7-600.webp",
      "/cooky/app-showcase/screenshot-8-600.webp",
      "/cooky/app-showcase/screenshot-11-600.webp",
      "/cooky/app-showcase/screenshot-12-600.webp",
    ],
    founderMessage: {
      videoUrl:
        "https://highwayfoundation.b-cdn.net/Miriam%20Rose%20-%20Website/tnt-test-vid.mp4",
      posterUrl: "/cooky/app-showcase/vid-cover-744.webp",
      fallbackText:
        "I built Cooky because weeknight dinner was the most stressful part of my day. If this does not make your life easier, email me and we'll make it right.",
      founderName: "Kaila",
      founderTitle: "Founder, Cooky",
    },
    confirmLabel: "Continue to secure checkout →",
    whyCardCopy:
      "You don't need a perfect kitchen or perfect habits. You need a plan that works when life is messy. Cooky helps you cook more at home with less friction.",
  },
  {
    id: "checkout_complete",
    order: 21,
    kind: "affirmation",
    icon: "✅",
    title: "Confirming your checkout and preparing your email update.",
    description: [
      "If this takes longer than a moment, tap Try again to refresh your Stripe status.",
      "Thanks for supporting Cooky.",
    ],
    confirmLabel: "Done",
  },
];

const resolveStepByFlow = (step: RawStep, flow: FlowVariant): FunnelStep => {
  switch (step.kind) {
    case "welcome":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        ratingText: resolveByFlow(step.ratingText, flow),
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
        trustBadgeCaption: step.trustBadgeCaption
          ? resolveByFlow(step.trustBadgeCaption, flow)
          : undefined,
        trustBadgeAvatars: step.trustBadgeAvatars
          ? resolveByFlow(step.trustBadgeAvatars, flow) || undefined
          : undefined,
        embeddedQuestion: step.embeddedQuestion
          ? resolveByFlow(step.embeddedQuestion, flow) || undefined
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        heroSlides: step.heroSlides
          ? resolveByFlow(step.heroSlides, flow) || undefined
          : undefined,
        featurePills: step.featurePills
          ? resolveByFlow(step.featurePills, flow) || undefined
          : undefined,
        finePrint: step.finePrint
          ? resolveByFlow(step.finePrint, flow)
          : undefined,
        featuredIn: step.featuredIn
          ? resolveByFlow(step.featuredIn, flow) || undefined
          : undefined,
      };
    case "question":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        helperText: step.helperText
          ? resolveByFlow(step.helperText, flow)
          : undefined,
        mediaLabel: step.mediaLabel
          ? resolveByFlow(step.mediaLabel, flow)
          : undefined,
        mediaImage: step.mediaImage
          ? resolveByFlow(step.mediaImage, flow) || undefined
          : undefined,
        options: resolveByFlow(step.options, flow),
        optionFeedback: step.optionFeedback,
      };
    case "affirmation":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
        mediaLabel: step.mediaLabel
          ? resolveByFlow(step.mediaLabel, flow)
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
      };
    case "loading":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        loadingFact: resolveByFlow(step.loadingFact, flow),
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
      };
    case "result":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        personaName: resolveByFlow(step.personaName, flow),
        description: resolveByFlow(step.description, flow),
        questMap: resolveByFlow(step.questMap, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
      };
    case "email": {
      const { secondaryCta: rawSecondaryCta, ...emailRest } = step;
      return {
        ...emailRest,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        description: resolveByFlow(step.description, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        ...(rawSecondaryCta != null
          ? { secondaryCta: resolveByFlow(rawSecondaryCta, flow) }
          : {}),
      };
    }
    case "payment":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        description: resolveByFlow(step.description, flow),
        benefits: resolveByFlow(step.benefits, flow),
        plans: resolveByFlow(step.plans, flow),
        proofBullets: resolveByFlow(step.proofBullets, flow),
        testimonials: resolveByFlow(step.testimonials, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        whyCardCopy: resolveByFlow(step.whyCardCopy, flow),
      };
  }
};

const getEnabledRawSteps = (flow: FlowVariant): RawStep[] => {
  return RAW_STEPS.filter((step) => {
    /** Flow B collects this on welcome; skip the duplicate standalone screen. */
    if (flow === "b" && step.id === "saved_recipes_friction") {
      return false;
    }

    if (step.id === "email_capture") {
      return config.enableEmailCapture;
    }

    return true;
  });
};

export const getFlowSteps = (flow: FlowVariant): FunnelStep[] => {
  return getEnabledRawSteps(flow).map((step) => resolveStepByFlow(step, flow));
};

export const TOTAL_STEPS = getEnabledRawSteps("a").length;

export const FLOW_META: Record<FlowVariant, { name: string; slug: string }> = {
  a: { name: "Quick meals", slug: "quick-meals" },
  b: { name: "Full kitchen", slug: "full-kitchen" },
};

export type FunnelSection = {
  label: string;
  /** inclusive 0-based start index */
  start: number;
  /** inclusive 0-based end index */
  end: number;
};

const BASE_FUNNEL_SECTIONS: FunnelSection[] = [
  { label: "Profile", start: 0, end: 6 },
  { label: "Personality", start: 7, end: 14 },
  { label: "Personalise", start: 15, end: 19 },
];

export const getFunnelSections = (steps: FunnelStep[]): FunnelSection[] => {
  const maxIndex = steps.length - 1;
  return BASE_FUNNEL_SECTIONS.map((section, index) => ({
    label: section.label,
    start: Math.min(section.start, maxIndex),
    end:
      index === BASE_FUNNEL_SECTIONS.length - 1
        ? maxIndex
        : Math.min(section.end, maxIndex),
  })).filter((section) => section.start <= section.end);
};

export const computeSectionProgress = (
  currentIndex: number,
  sections: FunnelSection[],
): number[] => {
  return sections.map(({ start, end }) => {
    const size = end - start + 1;
    const completed = Math.min(Math.max(currentIndex - start, 0), size);
    return completed / size;
  });
};
